export type InlineToken
  = | { kind: 'text'; value: string }
    | { kind: 'bold'; value: string }
    | { kind: 'italic'; value: string }
    | { kind: 'underline'; value: string }
    | { kind: 'strike'; value: string }
    | { kind: 'color'; value: string; color: string }
    | { kind: 'link'; value: string; href: string }

const TOKEN_RE = /(\[\[#[\da-fA-F]{6}\|[^\]]+\]\]|\*\*[^*]+\*\*|__[^_]+__|~~[^~]+~~|\*[^*]+\*|\[(?:[^\]]+)\]\((?:[^)]+)\))/g

export function isSafeHref(href: string): boolean {
  const trimmed = href.trim()
  if (!trimmed) return false
  if (trimmed.startsWith('/') && !trimmed.startsWith('//')) return true
  if (trimmed.startsWith('#')) return true
  try {
    const url = new URL(trimmed)
    return url.protocol === 'https:' || url.protocol === 'http:'
  }
  catch {
    return false
  }
}

export function tokenizeInline(input: string): InlineToken[] {
  if (!input) return []
  const tokens: InlineToken[] = []
  let last = 0
  const re = new RegExp(TOKEN_RE.source, 'g')
  let match: RegExpExecArray | null
  while ((match = re.exec(input))) {
    if (match.index > last) {
      tokens.push({ kind: 'text', value: input.slice(last, match.index) })
    }
    const raw = match[0]
    if (raw.startsWith('**') && raw.endsWith('**')) {
      tokens.push({ kind: 'bold', value: raw.slice(2, -2) })
    }
    else if (raw.startsWith('__') && raw.endsWith('__')) {
      tokens.push({ kind: 'underline', value: raw.slice(2, -2) })
    }
    else if (raw.startsWith('~~') && raw.endsWith('~~')) {
      tokens.push({ kind: 'strike', value: raw.slice(2, -2) })
    }
    else if (raw.startsWith('*') && raw.endsWith('*')) {
      tokens.push({ kind: 'italic', value: raw.slice(1, -1) })
    }
    else if (raw.startsWith('[[')) {
      const parsed = /^\[\[#([\da-fA-F]{6})\|([^\]]+)\]\]$/.exec(raw)
      if (parsed) tokens.push({ kind: 'color', color: `#${parsed[1]}`, value: parsed[2] ?? '' })
      else tokens.push({ kind: 'text', value: raw })
    }
    else if (raw.startsWith('[')) {
      const parsed = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(raw)
      const href = parsed?.[2]?.trim() ?? ''
      const label = parsed?.[1] ?? ''
      if (parsed && isSafeHref(href)) {
        tokens.push({ kind: 'link', value: label, href })
      }
      else {
        tokens.push({ kind: 'text', value: raw })
      }
    }
    last = match.index + raw.length
  }
  if (last < input.length) {
    tokens.push({ kind: 'text', value: input.slice(last) })
  }
  return tokens
}

export function stripInlineMarkup(input: string): string {
  return tokenizeInline(input)
    .map((token) => token.value)
    .join('')
}
